import React, {
  useEffect,
  useState,
} from 'react';
import Style from './style';
import {
  Grid,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
} from '@mui/material';
import logo from '../CardView/logo';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  Constants,
  Interfaces,
} from '../../../config';
import {
  InfoOutlined,
  Loop,
  Visibility,
  VisibilityOff,
  Save,
} from '@mui/icons-material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { DirectoryTree, } from '../DirectoryTree';
import LoadingComponent from '../../Loading';
import { NoDataV2, } from '../../NoDataV2';
import { useDSCalls, } from '../../../hooks';
import {
  StyledButton,
  StyledTextField,
} from '../../Reusable';

type Params = {
  oldName: string;
};

interface Props {
  schemaTypes: Array<string>;
}

function Azure(props: Props) {
  const classes = Style();
  const location = useLocation();
  const navigate = useNavigate();
  const inEditMode = location.pathname.includes('/edit');
  const { oldName, } = useParams<Params>();
  const { schemaTypes, } = props;
  const [expandedElements, setExpandedElements,] = useState<Array<string>>([]);
  const [exploreState, setExploreState,] = useState(true);
  const [showSasToken, setShowSasToken,] = useState<boolean>(false);

  const [state, setState,] = useState<Interfaces.AzureDataSource>({
    id: '',
    name: '',
    type: 'AZURE_BLOB',
    schemaType: schemaTypes[0],
    uri: '',
    sasToken: '',
    accountName: '',
    containerName: '',
  });
  const {
    dataSources,
    loading: loadingDS,
    testDataSource,
    dataSourceToEdit: dataSourceToEditHook,
    directories,
    loadingEdit,
    errorEdit,
    loadingTest,
    errorTest,
    loadingDirectories,
    errorDirectories,
    editDataSource,
    addDataSource,
    testNewDataSource,
    testExistingDataSource,
    fetchDirectories,
    setDataSourceToEdit,
    fetchNestedDirectory,
    setTestDataSource,
  } = useDSCalls();

  const dataSourceToEdit = dataSourceToEditHook as Interfaces.AzureDataSource | null;

  const endpoint = inEditMode && dataSourceToEdit
    ? `data-sources/${dataSourceToEdit.id}/azure_blob/dir`
    : Constants.api.endpoints.azureDirectories;

  const fetchDirectoriesAzure = (sasToken: string | null, uri: string | null) => {
    fetchDirectories({
      accountName: state.accountName,
      sasToken,
      containerName: state.containerName,
      uri,
    }, endpoint);
  };

  useEffect(() => {
    setTestDataSource(null);
    if (inEditMode) {
      if (dataSourceToEdit) {
        setState({
          ...state,
          id: dataSourceToEdit?.id,
          name: dataSourceToEdit?.name,
          type: dataSourceToEdit?.type,
          uri: dataSourceToEdit?.uri,
          schemaType: dataSourceToEdit?.schemaType,
          accountName: dataSourceToEdit?.accountName,
          containerName: dataSourceToEdit?.containerName,
          sasToken: null,
        });
        fetchDirectoriesAzure(null, dataSourceToEdit.uri === '' ? null : dataSourceToEdit.uri);

        setExploreState(false);
      } else {
        if (!dataSources) {
          return;
        }
        const dataSourceToChange = dataSources.find((item) => item.name === oldName) as Interfaces.AzureDataSource;
        setDataSourceToEdit(dataSourceToChange);
      }
    }
  }, [dataSourceToEdit, dataSources,]);


  const getTypeFromName = (directory: Interfaces.Directory) => {
    if (directory.isDirectory) {
      return 'DIRECTORY';
    }
    const extension = directory.path.split('.').pop();
    if (extension === 'csv') {
      return 'CSV';
    }
    return 'AVRO';
  };

  const getTypeFromUri = (uri: string) => {
    const extension = uri.split('.').pop();
    if (extension === 'csv') {
      return 'CSV';
    } else if (extension === 'avro') {
      return 'AVRO';
    }
    return 'DIRECTORY';
  };



  const handleChange = (event: any) => {
    const { name, value, } = event.target;
    setState({
      ...state,
      [name]: value,
    });
  };

  const isExploreDisabled =
    state.accountName === null
    || state.accountName === ''
    || state.sasToken === null
    || state.sasToken === ''
    || state.containerName === null
    || state.containerName === '';

  const onTestConnection = () => {
    inEditMode ? testExistingDataSource(state) : testNewDataSource(state);
  };

  const handleTestSuccess = () => {
    if (loadingTest) {
      return <CircularProgress
        color="inherit"
        style={{
          width: 11,
          height: 11,
        }}
      />;
    }
    if (testDataSource) {
      return <FiberManualRecordIcon style={{ fill: 'green', }} />;
    } else if (
      !testDataSource &&
      !errorTest
    ) {
      return <FiberManualRecordIcon style={{ fill: 'grey', }} />;
    } else if (
      !testDataSource &&
      errorTest
    ) {
      return <FiberManualRecordIcon style={{ fill: 'red', }} />;
    }
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const postSubmitAction = () => {navigate('/data-sources');};

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (inEditMode && oldName) {
      editDataSource( state, oldName, postSubmitAction);
    } else {
      addDataSource(state, postSubmitAction);
    }
  };

  if (loadingEdit) {
    return <LoadingComponent message={'Loading data source'}/>;
  }

  if (errorEdit) {
    return (
      <p>{errorEdit}</p>
    );
  }

  if (inEditMode && !dataSourceToEdit && !loadingDS) {
    return (
      <p>No data source was selected for edit mode</p>
    );
  }

  return (
    <form name="addAzureForm" onSubmit={handleSubmit}>
      <div className={classes.root}>
        <Grid container justifyContent='center'>
          <img
            src={logo.images.azure}
            alt="Google Bucket"
            className={classes.img}
          />
        </Grid>
        <Grid container spacing={3} justifyContent='center'>
          <Grid item xs={6}>
            <StyledTextField
              id="name"
              name="name"
              label="Name"
              value={state.name}
              variant="outlined"
              fullWidth
              size="small"
              onChange={(event: any) => {
                handleChange(event);
              }}
            />
            <StyledTextField
              className={classes.paddingBox}
              id="schemaType"
              name="schemaType"
              variant="outlined"
              value={state.schemaType}
              fullWidth
              size="small"
              onChange={handleChange}
              select
              SelectProps={{
                native: true,
              }}
            >
              {schemaTypes.map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </StyledTextField>
            <StyledTextField
              size={'small'}
              fullWidth
              value={state.accountName}
              onChange={(event: any) => {
                handleChange(event);
              }}
              label="Account name"
              name="accountName"
            />
            <StyledTextField
              size={'small'}
              fullWidth
              value={state.containerName}
              onChange={(event: any) => {
                handleChange(event);
              }}
              label="Container name"
              name="containerName"
            />
            <StyledTextField
              size={'small'}
              fullWidth
              type={showSasToken ? 'text' : 'password'}
              value={state.sasToken}
              onChange={(event: any) => {
                handleChange(event);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowSasToken(!showSasToken)}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showSasToken ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              label="Sas token"
              name="sasToken"
            />
          </Grid>
          <Grid item xs={6}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '4px',
              }}
            >
              <StyledTextField
                InputProps={{
                  endAdornment: (
                    <InputAdornment position={'end'}>
                      <Tooltip title={'Reload the buckets'} placement="top">
                        <IconButton
                          disabled={exploreState}
                          color={'primary'}
                          edge={'end'}
                          onClick={() => {
                            fetchDirectoriesAzure(state.sasToken, state.uri === '' ? null : state.uri);
                            setState({
                              ...state,
                              schemaType: getTypeFromUri(state.uri),
                            });
                          }}
                        >
                          <Loop />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                id="uri"
                name="uri"
                label="Uri"
                variant="outlined"
                value={state.uri}
                fullWidth
                size="small"
                onChange={(event: any) => {
                  handleChange(event);
                }}
              />
              <Tooltip title="Uri must start with 'wasbs://'" placement="top">
                <InfoOutlined
                  fontSize="small"
                  style={{ marginLeft: 10, }}
                />
              </Tooltip>
            </div>
            {
              exploreState
                ? (
                  <Box className={classes.treeBoxEmpty}>
                    <StyledButton
                      disabled={isExploreDisabled}
                      onClick={() => {
                        fetchDirectories({
                          accountName: state.accountName,
                          sasToken: state.sasToken,
                          containerName: state.containerName,
                          uri: state.uri === '' ? null : state.uri,
                        }, Constants.api.endpoints.azureDirectories);
                        setExploreState(false);
                      }}
                      color='primary'
                      variant='contained'
                      className={classes.exploreButton}
                    >
                      Explore
                    </StyledButton>
                    {
                      isExploreDisabled
                      && <Typography display={'block'} style={{ color: '#949494', }} variant={'caption'}>
                        Credentials required
                      </Typography>
                    }
                  </Box>
                )
                : (
                  <Box  className={classes.treeBox}>
                    {loadingDirectories && directories.length === 0 && <LoadingComponent size={60} message={'Loading bucket'} />}
                    {
                      errorDirectories && (
                        <div style={{ textAlign: 'center', }}>
                          <NoDataV2 message={errorDirectories} />
                          <StyledButton
                            onClick={() => {
                              fetchDirectories({
                                accountName: state.accountName,
                                sasToken: state.sasToken,
                                containerName: state.containerName,
                                uri: state.uri === '' ? null : state.uri,
                              }, Constants.api.endpoints.azureDirectories);
                              setExploreState(false);
                            }}
                            color='primary'
                            variant='contained'
                          >
                            Retry
                          </StyledButton>
                        </div>
                      )
                    }
                    {directories.length > 0 && !errorDirectories && (
                      <DirectoryTree
                        directories={directories}
                        isLoading={loadingDirectories}
                        expandedElements={expandedElements}
                        onTreeItemClick={(directory: Interfaces.Directory) => {
                          setState({
                            ...state,
                            uri: directory.path,
                            schemaType: getTypeFromName(directory),
                          });
                          if (directory.isDirectory) {
                            if (!directory.directories) {
                              fetchNestedDirectory({
                                accountName: state.accountName,
                                sasToken: state.sasToken,
                                containerName: state.containerName,
                                uri: state.uri === '' ? null : state.uri,
                              }, endpoint, directory.path);
                            }
                            if (expandedElements.includes(directory.path)) {
                              setExpandedElements([...expandedElements,].filter((expanded: string) => expanded !== directory.path));
                            } else {
                              setExpandedElements([...expandedElements, directory.path,]);
                            }
                          }
                        }}/>
                    )}
                  </Box>
                )
            }
          </Grid>
        </Grid>
        <div style={{ width: '100%', }}>
          <StyledButton
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            style={{ marginLeft: 0, }}
            startIcon={loadingDS ?
              <CircularProgress
                color="inherit"
                style={{
                  width: 16,
                  height: 11,
                }} />
              :
              <Save />}
          >
            Save
          </StyledButton>
          <StyledButton
            variant="outlined"
            size="large"
            className={classes.button}
            onClick={() => {
              navigate(-1);
            }}
          >
            Cancel
          </StyledButton>
          <StyledButton
            variant="contained"
            color="primary"
            size="large"
            style={{ float: 'right', }}
            startIcon={handleTestSuccess()}
            onClick={() => {
              onTestConnection();
            }}
          >
            Test
          </StyledButton>
        </div>
      </div>
    </form>
  );
}

export default Azure;
